<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'dashboard/components-next/polaris/ui/hover-card';

// Rebuilt inside on the kit's CountPopover (phase 9.8): the count is a button; hovering or focusing it shows the
// list (the registry hover card, so it opens from the keyboard too). Same props and `fetch` event (sent when
// the list opens, as the click did).
defineProps({
  count: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['fetch']);
</script>

<template>
  <HoverCard
    v-if="count"
    :open-delay="150"
    @update:open="open => open && emit('fetch')"
  >
    <HoverCardTrigger as-child>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        :aria-label="`${count} · ${title}`"
        data-count-popover
        @click="emit('fetch')"
      >
        <Icon :icon="icon" class="size-4" />
        {{ count }}
      </Button>
    </HoverCardTrigger>
    <HoverCardContent class="w-80" align="start">
      <p class="mb-3 flex items-center gap-2 type-polaris-label">
        <Icon :icon="icon" class="size-4" />
        {{ title }}
      </p>
      <div v-if="isFetching" class="flex items-center justify-center py-3">
        <Spinner />
      </div>
      <ul
        v-else
        role="list"
        class="m-0 grid max-h-72 list-none gap-2 overflow-y-auto p-0"
      >
        <li
          v-for="item in items"
          :key="item.id ?? item.key"
          class="flex min-w-0 items-center gap-2"
        >
          <Icon
            v-if="item.icon"
            :icon="item.icon"
            class="size-4 shrink-0 text-polaris-muted-foreground"
          />
          <Avatar
            v-else
            :src="item.avatarUrl"
            :name="item.name"
            :size="24"
            rounded-full
          />
          <span class="grid min-w-0">
            <span class="truncate type-polaris-body">
              {{ item.name }}
              <span v-if="item.id" class="text-polaris-muted-foreground">
                {{ `#${item.id}` }}
              </span>
            </span>
            <span
              v-if="item.email"
              class="truncate type-polaris-caption text-polaris-muted-foreground"
            >
              {{ item.email }}
            </span>
          </span>
        </li>
      </ul>
    </HoverCardContent>
  </HoverCard>
</template>
